import { Form, Formik } from 'formik';
import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Commands from '../../constants/commands';
import { MaintenanceSubCmds } from '../../constants/sub-commands';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { ButtonTypes } from '../../models/ButtonTypes';
import { CategoryRequest, deleteCategory, getCategories, getCategoryById, saveCategory } from '../../services/categoryService';
import { getCategoryFieldNames } from '../../services/customFieldsService';
import { executeCommandDeleteField } from '../../services/executeCommandService';
import { getErrorMessage, showError } from '../../services/utilities/ServiceError';
import { IAppState } from '../../store/types';
import CustomFieldsWizard from '../customFields/customFieldWizard';
import { DialogContent } from '../dialog/Dialog';
import { useDialog } from '../dialog/DialogContext';
import Wizard from '../wizard/wizard';
import { WizardSummaryContext } from '../wizard/wizardSummaryContext';
import DetailsForm, { DetailFields } from './detailsForm';
import FieldsForm from './fieldsForm';
import { SummaryForm } from './summaryForm';

interface WizardFields extends DetailFields {
    fields: string[];
    siteId?: number;
}
interface EnrolmentCategoryWizardProps {
    onClose: () => void;
    isOpen: boolean;
    setBusy: (isBusy) => void;
    categoryId?: number;
    totalEnrolments?: number;
};

const EnrolmentCategoryWizard: FC<EnrolmentCategoryWizardProps> = ({ onClose, isOpen, setBusy, categoryId = -1, totalEnrolments = undefined }) => {
    const initialState: WizardFields = {
        displayName: '',
        description: '',
        copyOf: '',
        singleOwner: false,
        autoIdNumber: false,
        fields: new Array(0)
    };
    const [step, setStep] = useState(0);
    const [categoryOptions, setCategoryOptions] = useState(new Array(0));
    const [fieldOptions, setFieldOptions] = useState(new Array(0));
    const [currentItem, setCurrentItem] = useState(initialState);
    const selectedSiteId = useSelector((state: IAppState) => state.site.site.value);
    const [summary, setSummary] = useState(new Array(0));
    const { translate } = useLocale();
    const [wizardTitle, setWizardTitle] = useState(translate.byKey(TranslationKeys.newCategory));
    const [oldFieldSelection, setOldFieldSelection] = useState(new Array(0));
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [refreshFields, setRefreshFields] = useState(false);
    const [sideBarSteps, setSideBarSteps] = useState([{
        title: translate.byKey(TranslationKeys.category),
        description: translate.byKey(TranslationKeys.describeCategory),
        substeps: []
    },
    {
        title: translate.byKey(TranslationKeys.fields),
        description: translate.byKey(TranslationKeys.selectApplicableFields),
        substeps: []
    },
    {
        title: translate.byKey(TranslationKeys.summary),
        description: '',
        substeps: []
    }]);
    const dialog = useDialog();

    useEffect(() => {
        buildCategories();
    }, [refreshFields]);

    const buildCategories = async () => {
        try{
            getCategories()
                .then(categories => {
                    const newCategoryOptions = categories.map(category => {
                        return {
                            value: category.id,
                            label: category.displayName
                        }
                    })
                    setCategoryOptions(newCategoryOptions)
                });

            const systemPersonAssetId = 8;
            const systemPersonAssetCategoryFieldNames = await getCategoryFieldNames(systemPersonAssetId);
            const defaultPersonAssetTemplateId = 9;
            const defaultPersonAssetTempalteCategoryFieldNames = await getCategoryFieldNames(defaultPersonAssetTemplateId);
            const distinctCategoryFieldNames = Object.values(systemPersonAssetCategoryFieldNames.concat(defaultPersonAssetTempalteCategoryFieldNames)
                .reduce((reducedList, currentFieldName, index) => {
                    if (reducedList[currentFieldName.value] === undefined) {
                        return {
                            ...reducedList,
                            [currentFieldName.value]: currentFieldName
                        }
                    }
                    return reducedList
                }, {}));
            distinctCategoryFieldNames.sort((a: any, b: any) => {
                return b.label < a.label ? 1 : -1;
            });
            setFieldOptions(distinctCategoryFieldNames);
            setRefreshFields(false)
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        if (categoryId > 0) {
            getCategoryById(categoryId)
                .then(category => {
                    setWizardTitle(translate.byKey(TranslationKeys.editCategory));
                    getCategoryFieldNames(categoryId)
                        .then(categoryFieldNames => {
                            const newFields = categoryFieldNames.map(categoryFieldName => categoryFieldName.value);
                            const newItem: WizardFields = {
                                displayName: category.displayName,
                                description: category.description ?? '',
                                copyOf: `${category.inheritsFromId}`,
                                siteId: category.siteId,
                                singleOwner: true,
                                autoIdNumber: false,
                                fields: newFields,
                            }

                            setOldFieldSelection(newFields);
                            setCurrentItem(newItem);
                        });
                })
                .catch(errorState => {
                    setWizardTitle(translate.byKey(TranslationKeys.newCategory));
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(errorState) })
                })
        }
    }, [categoryId])

    const onNext = (event: any) => {
        setStep(step + 1);
    };

    const onCustomFieldDelete = (id) => {
        const command = {
            cmd: Commands.Maintenance.label,
            cmdSubType: MaintenanceSubCmds.Delete_Master_Detail,
            entityId: id
        }

        executeCommandDeleteField(command)
            .then(() => {
                setRefreshFields(true)
                toast.success(translate.byKey(TranslationKeys.fieldDeleted));
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
    }

    const onFieldsSubmit = (formFields: WizardFields) => {
        setBusy(true);
        setCurrentItem(formFields);
        const newCategory: CategoryRequest = {
            id: categoryId,
            displayName: formFields.displayName,
            description: formFields.description,
            copyOf: formFields.copyOf,
            singleOwner: true,
            autoGenerateId: true,
            fields: formFields.fields ? formFields.fields : [],
            siteId: categoryId > 0 ? currentItem.siteId ?? selectedSiteId : selectedSiteId, // if there is no siteId give the currentUserSiteId,
            oldFields: oldFieldSelection.length > 0 ? oldFieldSelection : undefined
        };

        saveCategory(newCategory)
            .then((success) => {
                toast.success(translate.byKey(TranslationKeys.categorySaved));
                onWizardClose();
                setBusy(false);
            })
            .catch((failure) => {
                const message = failure['messageKey'] ? translate.byKey(failure['messageKey'])
                    : failure['message'] ? failure['message']
                    : translate.byKey(TranslationKeys.applicationError);
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message })
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const onBack = () => {
        setStep(step - 1)
    };

    const onWizardClose = () => {
        setStep(0);
        setCurrentItem(initialState);
        onClose();
    }

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            return { ...step, description: stepIndex === index ? newDescription : step.description };
        });
        setSideBarSteps(newSideBarSteps);
    }

    const localDeleteCategory = () => {
        if (categoryId) {
            setBusy(true);
            deleteCategory(categoryId)
                .then(() => {
                    onWizardClose();
                    toast.success(translate.byKey(TranslationKeys.deleteSuccess))
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                })
                .finally(() => { setBusy(false) });
        }
    }

    const handleDelete = () => {
        const dialogContent: DialogContent = {
            title: translate.byKeyFormatted(TranslationKeys.deleteRecordWithName, [currentItem.displayName]),
            message: translate.byKey(TranslationKeys.permanentlyDeleteRecord),
            buttons: [
                {
                    id: "btn_deleteCategory",
                    onClick: localDeleteCategory,
                    text: translate.byKey(TranslationKeys.confirm.toUpperCase()),
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "btn_Cancel",
                    onClick: () => { }, // Empty onClick will just close the dialog
                    text: translate.byKey(TranslationKeys.cancel),
                    type: ButtonTypes.TextButton,
                    className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                }
            ]
        }
        dialog.pushInfo(dialogContent);
    }

    const contentPages = [
        <DetailsForm
            key="newCategoryDetails"
            onNext={onNext}
            onDelete={handleDelete}
            totalEnrolments={totalEnrolments}
            categoryId={categoryId > 0 ? categoryId : undefined}
            options={categoryOptions}
            setSideBarStepDescription={newDescription => setSideBarStepDescription(0, newDescription)}>
        </DetailsForm>,
        <FieldsForm
            customDelete={{ fieldName: "isDefault", comparisonValue: 0 }}
            onDelete={onCustomFieldDelete}
            setShowFieldsModal={setShowFieldsModal}
            key="newCategoryFields"
            onBack={onBack}
            onNext={onNext}
            options={fieldOptions}
            setSideBarStepDescription={newDescription => setSideBarStepDescription(1, newDescription)}>
        </FieldsForm>,
        <SummaryForm
            key="newCategorySummary"
            categoryId={categoryId > 0 ? categoryId : undefined}
            onBack={onBack}>
        </SummaryForm>
    ];

    const updateSummary = (accessor: string, newDisplayValue: string) => {
        if (summary) {
            const existingSummaryEntryIndex = summary.findIndex(item => item.value === accessor);
            const newSummaryEntry = { value: accessor, label: newDisplayValue };
            if (existingSummaryEntryIndex >= 0) {
                summary.splice(existingSummaryEntryIndex, 1, newSummaryEntry);
                setSummary([...summary]);
            }
            else {
                summary.push(newSummaryEntry);
                setSummary([...summary]);
            }
        }
    }

    const getContextValue = useCallback(
        () => (
            {
                summary: [...summary],
                updateSummary
            }
        ),
        [summary, setSummary],
    )

    return <>
        {showFieldsModal && <CustomFieldsWizard
            isOpen={showFieldsModal}
            setBusy={setBusy}
            setRefreshFields={setRefreshFields}
            onClose={() => setShowFieldsModal(false)} />} <WizardSummaryContext.Provider value={getContextValue()}>
            <Modal
                isOpen={isOpen}
                ariaHideApp={false}
                overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
                className="bg-white border-none m-auto shadow select-none outline-none w-content">
                <Formik
                    enableReinitialize={true}
                    initialValues={currentItem}
                    onSubmit={onFieldsSubmit}>
                    <Form>
                        <Wizard
                            name={wizardTitle}
                            step={step}
                            onClose={onWizardClose}
                            sidebarSteps={sideBarSteps}>
                            {contentPages}
                        </Wizard>
                    </Form>
                </Formik>
            </Modal>
        </WizardSummaryContext.Provider>
    </>;
}

export default EnrolmentCategoryWizard;


